/* ==========================================================
   ESSENCE SEARCH INFINITE SCROLL — V2
   Chez Mishel / Shopify Prestige

   - Search pages only; collection infinite scroll is untouched.
   - Always requests the clean buyer query from Shopify.
   - Never depends on Strict Search or rewrites window.fetch.
   - Preserves native pagination as the no-JS / error fallback.
   - Exposes loadAll() so non-native sorts are globally correct.
========================================================== */

(() => {
  'use strict';

  if (window.__essenceSearchInfiniteScrollLoaded) return;
  window.__essenceSearchInfiniteScrollLoaded = true;

  const SEARCH_PATH_RE = /\/search\/?$/u;
  const SECTION_SELECTOR = '.shopify-section--main-search';
  const PRODUCT_LIST_SELECTOR = 'product-list[id^="product-list-"]';
  const PRODUCT_ITEM_SELECTOR = '.essence-search-sort-item';
  const PAGINATION_SELECTOR = '.pagination';
  const NEXT_LINK_SELECTOR = '.pagination a[rel="next"]';
  const STATUS_CLASS = 'essence-search-infinite-scroll-status';
  const ACTIVE_CLASS = 'essence-search-infinite-scroll--active';
  const ERROR_CLASS = 'essence-search-infinite-scroll--error';
  const INTERNAL_SECTION_PARAM = 'section_id';
  const ROOT_MARGIN = '900px 0px';
  const MAX_BULK_PAGES = 250;
  const NATIVE_SORTS = new Set([
    'relevance',
    'price-ascending',
    'price-descending'
  ]);

  const TEXT = {
    loading: 'טוען מוצרים נוספים…',
    loadingAll: 'טוען את כל תוצאות החיפוש כדי למיין בצורה מדויקת…',
    error: 'לא הצלחנו לטעון מוצרים נוספים. אפשר להמשיך באמצעות העמודים.'
  };

  const stateBySection = new WeakMap();
  let refreshTimer = null;

  const normalizeWhitespace = (value) =>
    String(value || '').replace(/\s+/gu, ' ').trim();

  const getBuyerQuery = () => {
    try {
      return normalizeWhitespace(
        new URL(window.location.href).searchParams.get('q')
      );
    } catch (error) {
      return '';
    }
  };

  const getActiveSort = () => {
    try {
      return (
        new URL(window.location.href).searchParams.get('sort_by') ||
        'relevance'
      );
    } catch (error) {
      return 'relevance';
    }
  };

  const createStatus = () => {
    const status = document.createElement('div');
    status.className = STATUS_CLASS;
    status.dataset.state = 'idle';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');

    const spinner = document.createElement('span');
    spinner.className = 'essence-search-infinite-scroll__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    spinner.hidden = true;

    const text = document.createElement('span');
    text.className = 'essence-search-infinite-scroll__text';

    status.append(spinner, text);
    return status;
  };

  const setStatus = (state, mode, message = '') => {
    if (!state.status || !state.statusText) return;

    state.status.dataset.state = mode;
    state.statusText.textContent = message;

    if (state.spinner) {
      state.spinner.hidden = mode !== 'loading';
    }

    state.status.hidden = mode === 'finished';
  };

  const revealPaginationFallback = (state) => {
    state.shopifySection.classList.remove(ACTIVE_CLASS);
    state.shopifySection.classList.add(ERROR_CLASS);
  };

  const cleanupState = (state) => {
    state.observer?.disconnect();
    state.abortController?.abort();

    if (state.status?.isConnected) {
      state.status.remove();
    }

    state.shopifySection.classList.remove(ACTIVE_CLASS, ERROR_CLASS);
  };

  const getNextUrl = (state) => {
    const link = state.shopifySection.querySelector(NEXT_LINK_SELECTOR);
    return link instanceof HTMLAnchorElement ? link.href : null;
  };

  const cleanBuyerSearchUrl = (sourceUrl, buyerQuery) => {
    const cleanUrl = new URL(sourceUrl, window.location.origin);
    const activeSort = getActiveSort();

    cleanUrl.searchParams.delete(INTERNAL_SECTION_PARAM);
    cleanUrl.searchParams.set('q', buyerQuery);

    if (activeSort && activeSort !== 'relevance') {
      cleanUrl.searchParams.set('sort_by', activeSort);
    } else {
      cleanUrl.searchParams.delete('sort_by');
    }

    return cleanUrl;
  };

  const sanitizePaginationLinks = (scope, buyerQuery) => {
    scope.querySelectorAll(`${PAGINATION_SELECTOR} a[href]`).forEach((anchor) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;

      try {
        const targetUrl = new URL(anchor.href, window.location.origin);

        if (
          targetUrl.origin === window.location.origin &&
          SEARCH_PATH_RE.test(targetUrl.pathname)
        ) {
          anchor.href = cleanBuyerSearchUrl(
            targetUrl,
            buyerQuery
          ).toString();
        }
      } catch (error) {
        // Keep Shopify's generated URL if it cannot be parsed.
      }
    });
  };

  const buildInternalRequestUrl = (nextUrl, state) => {
    const requestUrl = new URL(nextUrl, window.location.origin);
    const activeSort = getActiveSort();

    requestUrl.hash = '';
    requestUrl.searchParams.set(INTERNAL_SECTION_PARAM, state.sectionId);
    requestUrl.searchParams.set('q', getBuyerQuery());

    if (NATIVE_SORTS.has(activeSort) && activeSort !== 'relevance') {
      requestUrl.searchParams.set('sort_by', activeSort);
    } else {
      requestUrl.searchParams.delete('sort_by');
    }

    return requestUrl;
  };

  const extractRenderedSection = (html, sectionId) => {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    return (
      parsedDocument.getElementById(`shopify-section-${sectionId}`) ||
      parsedDocument.querySelector(SECTION_SELECTOR)
    );
  };

  const replacePagination = (state, newSection, buyerQuery) => {
    const currentPagination =
      state.shopifySection.querySelector(PAGINATION_SELECTOR);
    const newPagination = newSection.querySelector(PAGINATION_SELECTOR);

    if (newPagination) {
      sanitizePaginationLinks(newPagination, buyerQuery);
    }

    if (currentPagination && newPagination) {
      currentPagination.replaceWith(
        document.importNode(newPagination, true)
      );
    } else if (currentPagination) {
      currentPagination.remove();
    } else if (newPagination) {
      state.shopifySection
        .querySelector('.collection__main')
        ?.appendChild(document.importNode(newPagination, true));
    }
  };

  const prepareLoadedProduct = (item) => {
    const revealNodes = [
      item,
      ...item.querySelectorAll('[reveal-on-scroll], [reveal]')
    ];

    revealNodes.forEach((node) => {
      node.removeAttribute('reveal-on-scroll');
      node.removeAttribute('reveal');
      node.removeAttribute('hidden');
      node.style.setProperty('opacity', '1', 'important');
      node.style.setProperty('visibility', 'visible', 'important');
    });

    return item;
  };

  const collectLoadedHandles = (productList) =>
    new Set(
      Array.from(productList.querySelectorAll(PRODUCT_ITEM_SELECTOR))
        .map((item) => item.dataset.essenceSearchHandle || '')
        .filter(Boolean)
    );

  const syncLoadedHandles = (state) => {
    state.loadedHandles = collectLoadedHandles(state.productList);
  };

  const getNextOriginalIndex = (productList) => {
    const indexes = Array.from(
      productList.querySelectorAll(PRODUCT_ITEM_SELECTOR)
    )
      .map((item) => Number(item.dataset.essenceSearchOriginalIndex))
      .filter(Number.isFinite);

    return indexes.length ? Math.max(...indexes) + 1 : 0;
  };

  const dispatchProductsAppended = (state, count) => {
    document.dispatchEvent(
      new CustomEvent('essence:search:products-appended', {
        detail: {
          sectionId: state.sectionId,
          count,
          total: state.productList.querySelectorAll(
            PRODUCT_ITEM_SELECTOR
          ).length
        }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:end', { bubbles: true })
    );
  };

  const placeStatus = (state) => {
    const collectionMain =
      state.shopifySection.querySelector('.collection__main');
    const pagination =
      state.shopifySection.querySelector(PAGINATION_SELECTOR);

    if (!collectionMain) return false;

    if (pagination) {
      collectionMain.insertBefore(state.status, pagination);
    } else {
      collectionMain.appendChild(state.status);
    }

    return true;
  };

  const loadNextPage = async (state, options = {}) => {
    const bulk = Boolean(options.bulk);

    if (!state || state.loading || state.finished || state.failed) {
      return {
        status: state?.failed
          ? 'error'
          : state?.loading
            ? 'busy'
            : 'idle',
        appended: 0
      };
    }

    const nextUrl = getNextUrl(state);

    if (!nextUrl) {
      state.finished = true;
      state.observer?.disconnect();
      setStatus(state, 'finished');
      return { status: 'finished', appended: 0 };
    }

    const requestUrl = buildInternalRequestUrl(nextUrl, state);
    const requestKey = requestUrl.toString();

    if (state.requestedUrls.has(requestKey)) {
      state.failed = true;
      state.observer?.disconnect();
      revealPaginationFallback(state);
      setStatus(state, 'error', TEXT.error);
      console.error(
        '[Essence Search Infinite Scroll] Pagination did not advance.',
        requestKey
      );
      return { status: 'error', appended: 0 };
    }

    const buyerQueryAtStart = getBuyerQuery();
    state.loading = true;
    state.abortController = new AbortController();
    setStatus(
      state,
      'loading',
      bulk ? TEXT.loadingAll : TEXT.loading
    );

    try {
      const response = await fetch(requestKey, {
        credentials: 'same-origin',
        signal: state.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();

      if (
        buyerQueryAtStart !== getBuyerQuery() ||
        !state.productList.isConnected
      ) {
        return { status: 'stale', appended: 0 };
      }

      const newSection = extractRenderedSection(html, state.sectionId);
      const newProductList =
        newSection?.querySelector(PRODUCT_LIST_SELECTOR);

      if (!newSection || !newProductList) {
        throw new Error('The next search section could not be parsed.');
      }

      syncLoadedHandles(state);
      const incomingItems = Array.from(newProductList.children).filter(
        (item) => item.classList.contains('essence-search-sort-item')
      );

      state.requestedUrls.add(requestKey);

      const fragment = document.createDocumentFragment();
      const nextOriginalIndex = getNextOriginalIndex(state.productList);
      let appendedCount = 0;

      incomingItems.forEach((item) => {
        const handle = item.dataset.essenceSearchHandle || '';

        if (handle && state.loadedHandles.has(handle)) return;

        const importedItem = document.importNode(item, true);
        importedItem.dataset.essenceSearchOriginalIndex = String(
          nextOriginalIndex + appendedCount
        );
        prepareLoadedProduct(importedItem);

        if (handle) {
          state.loadedHandles.add(handle);
        }

        fragment.appendChild(importedItem);
        appendedCount += 1;
      });

      if (appendedCount > 0) {
        state.productList.appendChild(fragment);
      }

      replacePagination(state, newSection, getBuyerQuery());

      if (appendedCount > 0) {
        dispatchProductsAppended(state, appendedCount);
      }

      if (!getNextUrl(state)) {
        state.finished = true;
        state.observer?.disconnect();
        setStatus(state, 'finished');
        return { status: 'finished', appended: appendedCount };
      }

      if (!bulk) {
        setStatus(state, 'idle');
      }

      return { status: 'more', appended: appendedCount };
    } catch (error) {
      if (error?.name === 'AbortError') {
        return { status: 'aborted', appended: 0 };
      }

      state.failed = true;
      console.error('[Essence Search Infinite Scroll]', error);
      state.observer?.disconnect();
      revealPaginationFallback(state);
      setStatus(state, 'error', TEXT.error);
      return { status: 'error', appended: 0 };
    } finally {
      state.loading = false;
      state.abortController = null;
    }
  };

  const createState = (shopifySection) => {
    if (
      !(shopifySection instanceof HTMLElement) ||
      !shopifySection.id.startsWith('shopify-section-') ||
      !SEARCH_PATH_RE.test(window.location.pathname)
    ) {
      return null;
    }

    const buyerQuery = getBuyerQuery();
    const productList =
      shopifySection.querySelector(PRODUCT_LIST_SELECTOR);

    if (!buyerQuery || !(productList instanceof HTMLElement)) {
      return null;
    }

    const status = createStatus();
    const state = {
      shopifySection,
      sectionId: shopifySection.id.replace(/^shopify-section-/u, ''),
      productList,
      status,
      spinner: status.querySelector(
        '.essence-search-infinite-scroll__spinner'
      ),
      statusText: status.querySelector(
        '.essence-search-infinite-scroll__text'
      ),
      observer: null,
      abortController: null,
      loading: false,
      bulkLoading: false,
      finished: false,
      failed: false,
      requestedUrls: new Set(),
      loadedHandles: collectLoadedHandles(productList)
    };

    sanitizePaginationLinks(shopifySection, buyerQuery);

    if (!placeStatus(state)) return null;

    shopifySection.classList.remove(ERROR_CLASS);

    if (!getNextUrl(state)) {
      state.finished = true;
      setStatus(state, 'finished');
      return state;
    }

    state.observer = new IntersectionObserver(
      (entries) => {
        if (
          !state.bulkLoading &&
          entries.some((entry) => entry.isIntersecting)
        ) {
          void loadNextPage(state);
        }
      },
      {
        root: null,
        rootMargin: ROOT_MARGIN,
        threshold: 0
      }
    );

    shopifySection.classList.add(ACTIVE_CLASS);
    setStatus(state, 'idle');
    state.observer.observe(status);
    return state;
  };

  const initializeSection = (shopifySection) => {
    const existingState = stateBySection.get(shopifySection);
    const currentProductList =
      shopifySection.querySelector(PRODUCT_LIST_SELECTOR);

    if (
      existingState &&
      existingState.productList === currentProductList &&
      existingState.productList.isConnected &&
      existingState.status.isConnected
    ) {
      syncLoadedHandles(existingState);
      return existingState;
    }

    if (existingState) {
      cleanupState(existingState);
      stateBySection.delete(shopifySection);
    }

    const state = createState(shopifySection);

    if (state) {
      stateBySection.set(shopifySection, state);
    }

    return state;
  };

  const getActiveState = () => {
    const section = document.querySelector(SECTION_SELECTOR);
    return section instanceof HTMLElement
      ? stateBySection.get(section) || initializeSection(section)
      : null;
  };

  const refresh = () => {
    if (!SEARCH_PATH_RE.test(window.location.pathname)) return;
    document.querySelectorAll(SECTION_SELECTOR).forEach(initializeSection);
  };

  const waitForIdle = async (state) => {
    const startedAt = Date.now();

    while (state.loading && Date.now() - startedAt < 15000) {
      await new Promise((resolve) => window.setTimeout(resolve, 40));
    }

    return !state.loading;
  };

  const loadAll = async () => {
    refresh();
    const state = getActiveState();

    if (!state || state.finished || !getNextUrl(state)) {
      return { complete: true, pages: 0, appended: 0 };
    }

    state.bulkLoading = true;
    state.observer?.disconnect();
    setStatus(state, 'loading', TEXT.loadingAll);

    if (!(await waitForIdle(state))) {
      state.bulkLoading = false;
      revealPaginationFallback(state);
      setStatus(state, 'error', TEXT.error);
      return { complete: false, pages: 0, appended: 0 };
    }

    let pages = 0;
    let appended = 0;

    while (
      getNextUrl(state) &&
      !state.failed &&
      pages < MAX_BULK_PAGES
    ) {
      const result = await loadNextPage(state, { bulk: true });

      if (result.status === 'error' || result.status === 'aborted') {
        break;
      }

      if (result.status === 'busy') {
        await waitForIdle(state);
        continue;
      }

      appended += result.appended;
      pages += 1;
    }

    state.bulkLoading = false;
    const complete = !getNextUrl(state) && !state.failed;

    if (complete) {
      state.finished = true;
      setStatus(state, 'finished');
    } else if (!state.failed) {
      state.observer?.observe(state.status);
      setStatus(state, 'idle');
    }

    return { complete, pages, appended };
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, 60);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  const mutationObserver = new MutationObserver(scheduleRefresh);
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener('shopify:section:load', scheduleRefresh);
  document.addEventListener(
    'essence:search:candidates-enriched',
    scheduleRefresh
  );
  document.documentElement.addEventListener(
    'theme:loading:end',
    scheduleRefresh
  );
  window.addEventListener('popstate', scheduleRefresh);

  window.EssenceSearchInfiniteScroll = Object.freeze({
    version: '2.0.0',
    refresh,
    loadNextPage,
    loadAll
  });
})();